import { useRef, useState } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import useOutsideClick from '@rooks/use-outside-click';

import Logo from './Logo';
import Container from './Container';
import ImageS3 from './Image';

import { useAccountUser } from '../contexts/User';

function NavItem({ to, children, ...props }: LinkProps) {
  const className = props.className ?? 'ml-2 text-blue-300 hover:text-blue-100';

  // It is a simple element with nothing to link to
  if (!to)
    return (
      <span className={className} {...props}>
        {children}
      </span>
    );

  // It is intended to be an external link
  if (/^https?:\/\//.test(to.toString()))
    return (
      <a href={to.toString()} className={className} {...props}>
        {children}
      </a>
    );

  // Finally, it is an internal link
  return (
    <Link to={to} className={className} {...props}>
      {children}
    </Link>
  );
}

function UserNavItem({ children, ...props }: LinkProps) {
  const className = props.className ?? 'block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100';
  const role = props.role ?? 'menuitem';

  return (
    <Link className={className} role={role} {...props}>
      {children}
    </Link>
  );
}

export default function Nav() {
  const { user, signOut } = useAccountUser();

  const [showDdNav, setShowDdNav] = useState(false);
  const ddRef = useRef(document.createElement('div'));
  const hideDdNav = () => setShowDdNav(false);
  useOutsideClick(ddRef, hideDdNav);

  return (
    <nav>
      <Container>
        <div className="relative flex items-center justify-between pt-8 pb-12">
          <div className="flex items-center justify-center">
            <NavItem to="/">
              <Logo className="w-auto h-8" alt="API.Bible Logo" />
            </NavItem>
            <NavItem to="/bibles">Bibles</NavItem>
            <NavItem to="https://docs.api.bible">Documentation</NavItem>
            <NavItem to="/api-reference">API Reference</NavItem>
          </div>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
            {/* Profile dropdown */}
            {user && (
              <div ref={ddRef} className="relative ml-3">
                <div>
                  <button
                    type="button"
                    className="flex text-sm bg-gray-800 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-800 focus:ring-white"
                    id="user-menu"
                    aria-haspopup="true"
                    onClick={() => setShowDdNav(!showDdNav)}
                  >
                    <span className="sr-only">Open user menu</span>
                    {user.photo && (
                      <ImageS3
                        className="w-10 h-10 border-4 border-white rounded-full"
                        src={user.photo}
                        alt=""
                      />
                    )}
                    {!user.photo && user.name && (
                      <div className="flex items-center justify-center w-10 h-10 text-xl text-white bg-blue-500 border-4 border-white rounded-full">
                        <p>{user.name.slice(0, 1).toUpperCase()}</p>
                      </div>
                    )}
                  </button>
                </div>

                {/* Profile dropdown panel, show/hide based on dropdown state. */}
                <Transition
                  show={showDdNav}
                  as="div"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu"
                  className="absolute right-0 z-10 w-48 py-1 mt-2 bg-white shadow-lg origin-top-right rounded-md ring-1 ring-black ring-opacity-5"
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  {user.isSuperAdmin && (
                    <UserNavItem to="/super-admin/dashboard" onClick={hideDdNav}>
                      Super Admin
                    </UserNavItem>
                  )}
                  <UserNavItem to="/application" onClick={hideDdNav}>
                    Applications
                  </UserNavItem>
                  <UserNavItem to="/account-settings" onClick={hideDdNav}>
                    Account Settings
                  </UserNavItem>
                  <UserNavItem to="/user-settings" onClick={hideDdNav}>
                    User Settings
                  </UserNavItem>
                  <button
                    type="button"
                    className="block w-full px-4 py-2 text-sm text-left text-red-500 hover:bg-red-100"
                    role="menuitem"
                    onClick={() => signOut().then(() => hideDdNav)}
                  >
                    Sign out
                  </button>
                </Transition>
              </div>
            )}
          </div>
        </div>
      </Container>
    </nav>
  );
}
